import React, { memo } from 'react';
import KanbanCard from './KanbanCard';

/**
 * SwimlaneColumnDragOverlay Component
 * 
 * Renders a column overlay for swimlane mode drag.
 * Mirrors the actual column structure:
 * - Column header (matching swimlane sticky header style)
 * - Swimlane sections with background colors (matching swimlane row tints)
 * - Cards within each section
 * 
 * Each swimlane section has its background color tint but NO label/dot header
 * (those are only in the row label cell on the left).
 */
const SwimlaneColumnDragOverlay = memo(({ 
  column, 
  swimlanes, 
  tasksByStatus,
  frozenTaskCount,
  categories = [],
  allTags = [],
  nestedSprints = [],
  statuses = [],
  cardDisplaySettings = {},
  sortSetting = 'manual',
  maxSwimlanes = 4, // Max swimlanes to show
  maxCardsPerSwimlane = 3, // Max cards per swimlane
}) => {
  const totalCount = frozenTaskCount ?? tasksByStatus?.[column.id]?.length ?? 0;

  // Get visible swimlanes
  const visibleSwimlanes = swimlanes.slice(0, maxSwimlanes);
  const remainingSwimlanes = Math.max(0, swimlanes.length - maxSwimlanes);

  // Sort tasks by kanbanOrder if manual sorting (same as SwimlanesBoard)
  const sortTasks = (tasks) => {
    if (sortSetting === 'manual') {
      return [...tasks].sort((a, b) => (a.kanbanOrder ?? Infinity) - (b.kanbanOrder ?? Infinity));
    }
    return tasks;
  };

  // Helper to render a card
  const renderCard = (task) => {
    const category = task.category
      ? (allTags.find(t => t.name === (Array.isArray(task.category) ? task.category[0] : task.category)) ||
         categories.find(c => c.name === (Array.isArray(task.category) ? task.category[0] : task.category)))
      : null;
    const nestedSprint = task.nestedSprintId
      ? nestedSprints.find(s => s.id === task.nestedSprintId)
      : null;

    return (
      <KanbanCard
        task={task}
        category={category}
        allCategories={categories}
        allTags={allTags}
        nestedSprint={nestedSprint}
        statuses={statuses}
        isSelected={false}
        isDragging={false}
        isEditingTitle={false}
        cardDisplaySettings={cardDisplaySettings}
      />
    );
  };

  return (
    <div className="swimlane-column-drag-overlay">
      {/* Column Header - uses actual swimlane header classes for consistent styling */}
      <div className="kanban-swimlanes__header-column swimlane-column-drag-overlay__header">
        {/* Drag handle */}
        <span className="kanban-swimlanes__header-drag-handle">
          <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
            <circle cx="2" cy="2" r="1.5" />
            <circle cx="8" cy="2" r="1.5" />
            <circle cx="2" cy="7" r="1.5" />
            <circle cx="8" cy="7" r="1.5" />
            <circle cx="2" cy="12" r="1.5" />
            <circle cx="8" cy="12" r="1.5" />
          </svg>
        </span>
        <span 
          className="kanban-swimlanes__header-color" 
          style={{ backgroundColor: column.color }} 
        />
        <span className="kanban-swimlanes__header-label">{column.name}</span>
        <span className="kanban-swimlanes__header-count">{totalCount}</span>
      </div>

      {/* Swimlane sections - each with its background tint color */}
      <div className="swimlane-column-drag-overlay__body">
        {visibleSwimlanes.map(swimlane => {
          // Get tasks for this column in this swimlane, sorted properly
          const swimlaneTasks = sortTasks(
            (swimlane.tasks || []).filter(t => t.status === column.id)
          );
          const visibleTasks = swimlaneTasks.slice(0, maxCardsPerSwimlane);
          const hiddenCount = swimlaneTasks.length - visibleTasks.length;

          // Calculate the swimlane tint color (same as actual swimlane rows)
          const swimlaneTint = swimlane.color ? `${swimlane.color}15` : 'transparent';

          return (
            <div 
              key={swimlane.id} 
              className="swimlane-column-drag-overlay__section"
              style={{ background: swimlaneTint }}
            >
              {/* Cards - no swimlane label header, just cards */}
              {visibleTasks.length > 0 ? (
                <>
                  {visibleTasks.map(task => (
                    <div key={task.id} className="swimlane-column-drag-overlay__card-wrapper">
                      {renderCard(task)}
                    </div>
                  ))}
                  {hiddenCount > 0 && (
                    <div className="swimlane-column-drag-overlay__section-more">
                      +{hiddenCount} more
                    </div>
                  )}
                </>
              ) : (
                <div className="swimlane-column-drag-overlay__section-empty" />
              )}
            </div>
          );
        })}

        {remainingSwimlanes > 0 && (
          <div className="swimlane-column-drag-overlay__more-sections">
            +{remainingSwimlanes} more
          </div>
        )}
      </div>
    </div>
  );
});

export default SwimlaneColumnDragOverlay;

